/* eslint-disable camelcase */
import { fplsFetch } from '../../../../shared/utils';
import { logToServer } from '../../../form/utils';
import { actionTypes } from './stripe-reducer';

const { OTHERS } = actionTypes;
const FORMID = window.FORM_ID;
const genericSubscriptionErrorMessage = 'An error occured while trying to subscribe.';

const createLogPayload = (ctrl, transaction_id, transaction_amount, status, reason) => {
  const initialPayload = {
    form_id: FORMID,
    currency: ctrl.props.currency,
    gateway: 'Stripe',
  };
  const payload = {
    ...initialPayload,
    transaction_id,
    amount: transaction_amount,
    status,
    reason,
  };
  return payload;
};

// Create repeating error catch block or request error actions
const requestErrorAction = (
  resMessage,
  updateState,
  handleStorePaymentValue,
  selectedSubscriptionProductDetails,
  ctrl
) => {
  updateState(OTHERS, {
    paying: false,
    disableInputs: false,
  });

  handleStorePaymentValue('error', '', resMessage, '');

  const payload = createLogPayload(
    ctrl,
    'failure',
    selectedSubscriptionProductDetails.amount / ctrl.props.sub_unit,
    'failure',
    resMessage
  );

  logToServer(payload);
};

export const getStripeProducts = async (ctrl, setPlanDetails, updateState) => {
  if (!ctrl.props.shown_product) return;
  updateState(OTHERS, {
    loadingProducts: true,
  });
  try {
    const resp = await fplsFetch({
      url: `/api/v2/public/stripe/products?form_id=${FORMID}&product_id=${ctrl.props.shown_product}`,
      removeToken: true,
    });

    if (resp.plans) {
      resp.plans.forEach((plan) => {
        if (plan.active) {
          const option = () => {
            const hasNickname = plan.nickname ? ` - ${plan.nickname}` : '';
            if (plan.product && plan.product.name) {
              return `${plan.product.name}${hasNickname}`;
            }
            return `${plan.name}${hasNickname}`;
          };

          const image = () => {
            if (plan.product && plan.product.images && plan.product.images[0]) {
              return plan.product.images[0];
            }
            if (plan.images && plan.images[0]) {
              return plan.images[0];
            }
            return ctrl.props.payment_logo;
          };

          setPlanDetails((prev) => ({
            ...prev,
            options: [...prev.options, option()],
            images: [...prev.images, image()],
            subscriptionItems: [
              ...prev.subscriptionItems,
              {
                plan: plan.id,
                nickname: plan.nickname,
                amount: plan.amount,
                trial_period_days: plan.trial_period_days,
                one_time: plan.one_time || false,
              },
            ],
          }));
        }
      });
      updateState(OTHERS, {
        loadingProducts: false,
      });
    }
  } catch (error) {
    let errMsg = '';
    if (error) {
      errMsg = `Error: ${error.message}`;
      updateState(OTHERS, {
        loadingProductsError: errMsg,
      });
    } else {
      errMsg = 'An error has occurred when attempting to retrieve products from stripe.';
      updateState(OTHERS, {
        loadingProductsError: errMsg,
      });
    }
    setTimeout(() => {
      updateState(OTHERS, {
        loadingProductsError: '',
      });
    }, 3000);
    console.error('Error while retrieving products from stripe');
    updateState(OTHERS, {
      loadingProducts: false,
    });
  }
};

export const paySubscription = async (subPayload) => {
  const {
    ctrl,
    stripe,
    stripeCardElement,
    updateState,
    additionalData,
    paymentDetails,
    metadata,
    handleStorePaymentValue,
    stripeContext,
    selectedSubscriptionProductDetails,
    modeStatusText,
    submitButtonText,
  } = subPayload;
  let resMessage = '';
  let displayMsg = '';
  let value = '';
  const { amount } = stripeContext.lineItem;
  let isFunctionDone = false;

  try {
    let response = await stripe.createPaymentMethod({
      type: 'card',
      card: stripeCardElement,
      billing_details: ctrl.props.billing_address ? { address: additionalData } : null,
    });

    if (response.paymentMethod) {
      try {
        response = await fplsFetch({
          method: 'POST',
          url: `/api/v2/public/stripe/subscription/${FORMID}`,
          data: {
            form_id: FORMID,
            payment_method: response.paymentMethod.id,
            subscription_data: {
              item: selectedSubscriptionProductDetails,
            },
            payment_details: paymentDetails,
            address: ctrl.props.billing_address ? additionalData : {},
            metadata,
          },
          removeToken: true,
        });

        let data;
        if (response.pi_secret) {
          try {
            const result = await stripe.confirmCardPayment(response.pi_secret, {
              payment_method: {
                card: stripeCardElement,
                billing_details: {
                  name: paymentDetails.name,
                },
              },
              shipping: ctrl.props.billing_address
                ? {
                    address: additionalData,
                    name: paymentDetails.name,
                  }
                : null,
              save_payment_method: paymentDetails.save_card,
              setup_future_usage: 'off_session',
              receipt_email: paymentDetails.email,
            });

            if (result.error) {
              resMessage = result.error.message;

              updateState(OTHERS, {
                paying: false,
                disableInputs: false,
              });

              handleStorePaymentValue('error', '', resMessage, '');

              const payload = createLogPayload(
                ctrl,
                'error',
                amount / ctrl.props.sub_unit,
                result.error.message,
                resMessage
              );
              logToServer(payload);
              isFunctionDone = true;
            } else if (result.paymentIntent.status === 'succeeded') {
              // The payment has been processed!
              resMessage = 'Payment complete.';
              displayMsg = `Payment of ${result.paymentIntent.amount / ctrl.props.sub_unit} ${
                ctrl.props.currency
              } approved. Card charged successfully. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction.`;

              updateState(OTHERS, {
                paying: false,
                disableInputs: false,
              });

              value = `Amount paid: ${result.paymentIntent.amount / ctrl.props.sub_unit} ${
                ctrl.props.currency
              }\nTransaction ID: ${result.paymentIntent.id}`;

              handleStorePaymentValue('paid', displayMsg, resMessage, value, result.paymentIntent.id);

              const payload = createLogPayload(
                ctrl,
                result.paymentIntent.id,
                result.paymentIntent.amount / ctrl.props.sub_unit,
                `success ${modeStatusText}`,
                resMessage
              );
              logToServer(payload);
              isFunctionDone = true;
            }
          } catch (error) {
            resMessage = `${genericSubscriptionErrorMessage} [ ${error} ]`;
            requestErrorAction(
              resMessage,
              updateState,
              handleStorePaymentValue,
              selectedSubscriptionProductDetails,
              ctrl
            );
            isFunctionDone = true;
          }
        } else if (response.status === 'success') {
          data = JSON.parse(response.data);
          resMessage = 'Subscription created successfully.';
          displayMsg = `You have successfully subscribed to ${
            data.plan.nickname || data.plan.id
          } plan for ${data.plan.currency.toUpperCase()} ${data.plan.amount / ctrl.props.sub_unit}.`;

          updateState(OTHERS, {
            paying: false,
          });

          value =
            `Plan subscribed to: ${data.plan.nickname || data.plan.id}\n` +
            `Amount: ${data.plan.currency.toUpperCase()} ${data.plan.amount / ctrl.props.sub_unit}\n` +
            `Transaction ID: ${data.id}`;

          handleStorePaymentValue('paid', displayMsg, resMessage, value, data.id);

          const payload = createLogPayload(
            ctrl,
            data.id,
            data.plan.amount / ctrl.props.sub_unit,
            `success ${modeStatusText}`,
            resMessage
          );
          logToServer(payload);
          isFunctionDone = true;
        } else if (response.status === 'trialing') {
          data = JSON.parse(response.data);
          resMessage = 'You are currently on trial for the selected product.';
          displayMsg = `You have successfully subscribed to ${
            data.plan.nickname || data.plan.id
          } plan for ${data.plan.currency.toUpperCase()} ${
            data.plan.amount / ctrl.props.sub_unit
          } Currently on trial for ${data.plan.trial_period_days} days.`;

          updateState(OTHERS, {
            paying: false,
          });

          value =
            `Plan subscribed to: ${data.plan.nickname || data.plan.id}\n` +
            `Amount: ${data.plan.currency.toUpperCase()} ${data.plan.amount / ctrl.props.sub_unit}\n` +
            `Transaction ID: ${data.id}\n${data.plan.trial_period_days} days trial.`;

          handleStorePaymentValue('paid', displayMsg, resMessage, value, data.id);

          const payload = createLogPayload(
            ctrl,
            data.id,
            data.plan.amount / ctrl.props.sub_unit,
            `success ${modeStatusText}`,
            resMessage
          );
          logToServer(payload);
          isFunctionDone = true;
        } else if (response.status === 'failure') {
          resMessage = 'Failed to pay for the subscription.';

          updateState(OTHERS, {
            paying: false,
            disableInputs: false,
          });

          handleStorePaymentValue('error', '', resMessage, '');

          const payload = createLogPayload(
            ctrl,
            'failure',
            selectedSubscriptionProductDetails.amount / ctrl.props.sub_unit,
            `failure ${modeStatusText}`,
            resMessage
          );
          logToServer(payload);
          isFunctionDone = true;
        } else if (response.status === 'incomplete') {
          data = JSON.parse(response.data);
          stripe.confirmCardPayment(data.latest_invoice.payment_intent.client_secret).then((result) => {
            if (result.error) {
              resMessage = result.error.message;

              updateState(OTHERS, {
                paying: false,
                disableInputs: false,
              });

              handleStorePaymentValue('error', '', resMessage, '');

              const payload = createLogPayload(
                ctrl,
                'failure',
                selectedSubscriptionProductDetails.amount / ctrl.props.sub_unit,
                `failure ${modeStatusText}`,
                resMessage
              );
              logToServer(payload);
            } else {
              resMessage = 'Subscription created successfully.';
              displayMsg = `You have successfully subscribed to ${
                data.plan.nickname || data.plan.id
              } plan for ${data.plan.currency.toUpperCase()} ${data.plan.amount / ctrl.props.sub_unit}${
                data.plan.trial_period_days ? `. Currently on trial for ${data.plan.trial_period_days}` : ''
              }. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction.`;

              updateState(OTHERS, {
                paying: false,
              });

              value =
                `Plan subscribed to: ${data.plan.nickname || data.plan.id}\n` +
                `Amount: ${data.plan.currency.toUpperCase()} ${data.plan.amount / ctrl.props.sub_unit}\n` +
                `Transaction ID: ${data.id}\n${
                  data.plan.trial_period_days ? `${data.plan.trial_period_days} days trial` : ''
                }`;

              handleStorePaymentValue('paid', displayMsg, resMessage, value, data.id);

              const payload = createLogPayload(
                ctrl,
                result.paymentIntent.id,
                selectedSubscriptionProductDetails.amount / ctrl.props.sub_unit,
                `failure ${modeStatusText}`,
                resMessage
              );
              logToServer(payload);
              isFunctionDone = true;
            }
          });
        } else {
          resMessage = `${genericSubscriptionErrorMessage}`;
          requestErrorAction(
            resMessage,
            updateState,
            handleStorePaymentValue,
            selectedSubscriptionProductDetails,
            ctrl
          );
          isFunctionDone = true;
        }
      } catch (error) {
        resMessage = error.data.message.split(':').pop() || 'An error occured while trying to subscribe.';
        requestErrorAction(resMessage, updateState, handleStorePaymentValue, selectedSubscriptionProductDetails, ctrl);
        isFunctionDone = true;
      }
    }
  } catch (error) {
    resMessage = `${genericSubscriptionErrorMessage} [ ${error} ]`;
    requestErrorAction(resMessage, updateState, handleStorePaymentValue, selectedSubscriptionProductDetails, ctrl);
    isFunctionDone = true;
  }
  return isFunctionDone;
};

export const payCharge = async (chargePayload) => {
  const {
    ctrl,
    stripe,
    stripeCardElement,
    updateState,
    additionalData,
    paymentDetails,
    metadata,
    handleStorePaymentValue,
    stripeContext,
    modeStatusText,
    submitButtonText,
  } = chargePayload;
  let resMessage = '';
  let displayMsg = '';
  let value = '';
  const { amount, currency } = stripeContext.lineItem;
  let isFunctionDone = false;

  try {
    const response = await fplsFetch({
      method: 'POST',
      url: `/api/v2/public/stripe/payment_intent/${FORMID}`,
      data: {
        form_id: FORMID,
        amount,
        currency,
        email: paymentDetails.email,
        name: paymentDetails.name,
        description: paymentDetails.description,
        metadata,
        address: ctrl.props.billing_address ? additionalData : {},
      },
      removeToken: true,
    });

    if (response.pi_secret) {
      try {
        const result = await stripe.confirmCardPayment(response.pi_secret, {
          payment_method: {
            card: stripeCardElement,
            billing_details: {
              name: paymentDetails.name,
            },
          },
          shipping: ctrl.props.billing_address
            ? {
                address: additionalData,
                name: paymentDetails.name,
              }
            : null,
          save_payment_method: paymentDetails.save_card,
          setup_future_usage: 'off_session',
          receipt_email: paymentDetails.email,
        });

        if (result.error) {
          resMessage = result.error.message;

          updateState(OTHERS, {
            paying: false,
            disableInputs: false,
          });

          handleStorePaymentValue('error', '', resMessage, '');

          const payload = createLogPayload(
            ctrl,
            'error',
            amount / ctrl.props.sub_unit,
            `${result.error.message} ${modeStatusText}`,
            resMessage
          );
          logToServer(payload);
          isFunctionDone = true;
        } else if (result.paymentIntent.status === 'succeeded') {
          resMessage = 'Payment complete.';
          displayMsg = `Payment of ${result.paymentIntent.amount / ctrl.props.sub_unit} ${
            ctrl.props.currency
          } approved. Card charged successfully. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction.`;

          updateState(OTHERS, {
            paying: false,
            disableInput: false,
          });

          value =
            `Amount paid: ${result.paymentIntent.amount / ctrl.props.sub_unit} ${ctrl.props.currency}\n` +
            `Transaction ID: ${result.paymentIntent.id}`;
          handleStorePaymentValue('paid', displayMsg, resMessage, value, result.paymentIntent.id);

          const payload = createLogPayload(
            ctrl,
            result.paymentIntent.id,
            result.paymentIntent.amount / ctrl.props.sub_unit,
            `success ${modeStatusText}`,
            resMessage
          );

          logToServer(payload);
          isFunctionDone = true;
        }
      } catch (error) {
        console.log(error);
      }
    }
  } catch (error) {
    resMessage = error.message || 'An error occured while paying.';

    updateState(OTHERS, {
      paying: false,
      disableInputs: false,
    });

    handleStorePaymentValue('error', '', resMessage, '');

    const payload = createLogPayload(
      ctrl,
      'error',
      amount / ctrl.props.sub_unit,
      `failure ${modeStatusText}`,
      resMessage
    );
    logToServer(payload);
    isFunctionDone = true;
  }
  return isFunctionDone;
};
